/**
 * Which reports the signed-in user can open, from `/access`
 * section_permissions.reports. Mirrors the reports service
 * (reports_utils/access.py + the run endpoint's own-scope rule) so the portal
 * only offers reports the API will run:
 *
 * - every report is opened by one `reports` permission: `scope` for legacy
 *   reports and the Daily Sales Report, its own key for a summary
 *   (`access_permission`, e.g. `canViewSalesSummary`, `earningsSummaryScope`);
 * - a boolean permission is all-or-nothing; a scope one is none / own / all;
 * - an own-scoped user can't run a report that only shows location-wide
 *   totals (`supports_own_scope: false`).
 */
import type { ReportsPermissions } from "../types/featureAccess";

export type ReportAccessScope = "none" | "own" | "all";

/** The fields of a catalog definition that decide access. */
export interface ReportAccessInfo {
  access_permission?: string;
  supports_own_scope?: boolean;
}

/** Permission that opens a report when the definition doesn't say. */
export const DEFAULT_REPORT_PERMISSION = "scope";

/** Per-report permissions besides `scope` (see workflow_schema.py). */
const SUMMARY_PERMISSIONS = ["canViewSalesSummary", "earningsSummaryScope"];

function scopeOf(value: unknown): ReportAccessScope {
  if (value === true || value === "all") return "all";
  if (value === "own") return "own";
  return "none";
}

/** The user's scope for one report ("none" when they can't open it). */
export function reportAccessScope(
  perms: ReportsPermissions | null | undefined,
  info: ReportAccessInfo = {},
): ReportAccessScope {
  if (!perms) return "none";
  const key = info.access_permission ?? DEFAULT_REPORT_PERMISSION;
  const scope = scopeOf((perms as unknown as Record<string, unknown>)[key]);
  if (scope === "own" && info.supports_own_scope === false) return "none";
  return scope;
}

/** True when the API would run this report for the user. */
export const canOpenReport = (
  perms: ReportsPermissions | null | undefined,
  info: ReportAccessInfo = {},
): boolean => reportAccessScope(perms, info) !== "none";

/**
 * True when the user can open at least one report, i.e. the Reports section
 * (nav entry, route, saved-report list) is theirs. An Assistant with no
 * reports scope still reaches it through their own Earnings Summary.
 */
export function hasAnyReportAccess(
  perms: ReportsPermissions | null | undefined,
): boolean {
  if (!perms) return false;
  return [DEFAULT_REPORT_PERMISSION, ...SUMMARY_PERMISSIONS].some(
    (key) =>
      scopeOf((perms as unknown as Record<string, unknown>)[key]) !== "none",
  );
}
